import React, { useCallback, useMemo } from "react";
import { InputCard } from "components/InputCards/InputCard";
import { Select } from "components/Select/Select";
import type { TechnicalPropertyStrict } from "stores/product";
import { useProductActions } from "stores/product";
import type { ChangeInputPayload } from "interfaces/shared.types";
import { z } from "zod";

const labelReplace = (str: string | null) =>
  str?.replace(
    /\\([a-zA-Z0-9,]+)\\s*/g, // change the stringformat a\\b\\ to a<sub>b</sub>
    "<sub>$1</sub>",
  ) ?? str;

const PropertyValueSchema = z.object({
  key: z.string(),
  value: z.number(),
});

export const Property = ({
  property,
  isValueEditable,
  propertyStyles,
}: {
  property: TechnicalPropertyStrict;
  isValueEditable: boolean;
  propertyStyles?: string;
}) => {
  const productActions = useProductActions();
  const propertyValues = useMemo(
    () =>
      Array.isArray(property.values)
        ? property.values
            .filter((val) => PropertyValueSchema.safeParse(val).success)
            .map((val) => PropertyValueSchema.parse(val))
        : [],
    [property.values],
  );

  const options = useMemo(
    () =>
      propertyValues.map((opt) => ({
        label: opt.key,
        value: opt.value,
      })),
    [propertyValues],
  );

  const selectedOption = useMemo(
    () => options && options.find((opt) => opt.value === property.value),
    [options, property.value],
  );

  const subLabel = useMemo(
    () =>
      property.valueMin !== null &&
      `${property.valueMin} ${
        property.valueMax !== null ? `.. ${property.valueMax}` : ".."
      } ${property.unit}`,
    [property.unit, property.valueMax, property.valueMin],
  );

  const handleChange = useCallback(
    (inputValue: number | ChangeInputPayload) => {
      if (property.id) {
        const value =
          typeof inputValue === "object"
            ? inputValue.changeInput.inputValue
            : String(inputValue);
        productActions.setTechnicalProperties([
          {
            id: property.id,
            value,
          },
        ]);
      }
    },
    [productActions, property.id],
  );

  return (
    <>
      {propertyValues?.length ? (
        <div className={propertyStyles}>
          <div className="font-md ps-2">
            {labelReplace(property.presentation)}
          </div>
          {propertyValues.length > 1 && (
            <Select
              options={options}
              value={selectedOption}
              onChange={(opt) => opt && handleChange(opt.value)}
              testId={property.id}
            />
          )}
          {propertyValues.length === 1 && (
            <div className="align-items-start" style={{ minWidth: "6.5rem" }}>
              {propertyValues[0].value}
            </div>
          )}
        </div>
      ) : (
        <InputCard
          label={`${labelReplace(property.presentation)} ${labelReplace(property.symbol)}`}
          subLabel={subLabel}
          value={property.value ?? 0}
          displayValue={property.displayValue}
          valueMin={property.valueMin}
          valueMax={property.valueMax}
          unit={property.unit}
          isValueEditable={isValueEditable}
          onChange={handleChange}
          testId={`${property.id}`}
          validationMessage={property.validationMessage ?? ""}
          validationType={property.validationType}
          defaultValue={0}
        />
      )}
    </>
  );
};
